/**
 * Forms API utilities
 */

import { API_BASE_URL } from '../config/api';
import { getAuthHeaders } from './authUtils';

// Types
export type FormFieldType = 'text' | 'textarea' | 'checkbox' | 'select' | 'date';
export type FormType = 'general' | 'intake';
export type FormSubmissionStatus = 'pending' | 'submitted' | 'reviewed';

export interface FormFieldDefinition {
  id: string;
  type: FormFieldType;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
  help_text?: string;
}

export interface FormTemplate {
  id: string;
  practice_id: string;
  name: string;
  description?: string;
  form_type: FormType;
  fields: FormFieldDefinition[];
  intake_mapping?: Record<string, string>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface FormSubmission {
  id: string;
  form_template_id: string;
  practice_id: string;
  encounter_id?: string;
  encounter_date?: string;
  pet_owner_id?: string;
  pet_id?: string;
  responses: Record<string, any>;
  status: FormSubmissionStatus;
  submitted_at?: string;
  reviewed_at?: string;
  reviewed_by_user_id?: string;
  created_at: string;
  updated_at: string;
  template?: FormTemplate;
}

export interface PublicFormView {
  submission_id: string;
  template_name: string;
  template_description?: string;
  fields: FormFieldDefinition[];
  responses: Record<string, any>;
  status: string;
  is_submitted: boolean;
}

// API Functions

// Templates
export async function getFormTemplates(activeOnly = true, formType?: FormType): Promise<FormTemplate[]> {
  const params = new URLSearchParams();
  params.append('active_only', String(activeOnly));
  if (formType) params.append('form_type', formType);
  
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates?${params}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch form templates');
  return response.json();
}

export async function getFormTemplate(templateId: string): Promise<FormTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/${templateId}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch form template');
  return response.json();
}

export async function createFormTemplate(data: {
  name: string;
  description?: string;
  form_type: FormType;
  fields: FormFieldDefinition[];
  intake_mapping?: Record<string, string>;
  is_active?: boolean;
}): Promise<FormTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) throw new Error('Failed to create form template');
  return response.json();
}

export async function updateFormTemplate(
  templateId: string,
  data: Partial<{
    name: string;
    description?: string;
    form_type: FormType;
    fields: FormFieldDefinition[];
    intake_mapping?: Record<string, string>;
    is_active?: boolean;
  }>
): Promise<FormTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/${templateId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) throw new Error('Failed to update form template');
  return response.json();
}

export async function deleteFormTemplate(templateId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/${templateId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to delete form template');
}

// Submissions
export async function getFormSubmissions(
  statusFilter?: FormSubmissionStatus,
  limit = 100
): Promise<FormSubmission[]> {
  const params = new URLSearchParams();
  if (statusFilter) params.append('status_filter', statusFilter);
  params.append('limit', String(limit));
  
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/submissions?${params}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch form submissions');
  return response.json();
}

export async function getPetForms(petId: string): Promise<FormSubmission[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/pets/${petId}/forms`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch pet forms');
  return response.json();
}

export async function deleteFormSubmission(submissionId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/submissions/${submissionId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to delete form submission');
}

export async function getFormSubmission(submissionId: string): Promise<FormSubmission> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/submissions/${submissionId}`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch form submission');
  return response.json();
}

export async function createFormSubmission(data: {
  form_template_id: string;
  encounter_id?: string;
  pet_owner_id?: string;
  pet_id?: string;
}): Promise<FormSubmission> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/submissions`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) throw new Error('Failed to create form submission');
  return response.json();
}

export async function markSubmissionReviewed(submissionId: string): Promise<FormSubmission> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/submissions/${submissionId}/mark-reviewed`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to mark submission as reviewed');
  return response.json();
}

// Public endpoints (no auth)
export async function getPublicForm(submissionId: string): Promise<PublicFormView> {
  const response = await fetch(`${API_BASE_URL}/api/v1/public/forms/${submissionId}`);
  
  if (!response.ok) throw new Error('Form not found');
  return response.json();
}

export async function submitPublicForm(
  submissionId: string,
  responses: Record<string, any>
): Promise<{ success: boolean; message: string; submission_id: string }> {
  const response = await fetch(`${API_BASE_URL}/api/v1/public/forms/${submissionId}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ responses }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to submit form');
  }
  return response.json();
}

// Encounter intake form
export async function getEncounterIntakeForm(encounterId: string): Promise<FormSubmission> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/intake-form`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Intake form not found');
  return response.json();
}

// Create intake form for existing encounter (auto-creates template from encounter templates)
export async function createIntakeFormForEncounter(
  encounterId: string,
  practiceId: string,
  ownerId: string,
  petId: string
): Promise<FormSubmission> {
  const response = await fetch(`${API_BASE_URL}/api/v1/encounters/${encounterId}/create-intake-form`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      practice_id: practiceId,
      owner_id: ownerId,
      pet_id: petId,
    }),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create intake form');
  }
  return response.json();
}

// Get intake fields from encounter section templates (for form builder auto-populate)
export async function getIntakeFieldsPreview(): Promise<{
  fields: FormFieldDefinition[];
  intake_mapping: Record<string, string>;
}> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/intake/fields`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to get intake fields');
  }
  return response.json();
}

// Auto-create intake template from encounter section templates
export async function autoCreateIntakeTemplate(): Promise<FormTemplate> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/intake/auto-create`, {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to auto-create intake template');
  }
  return response.json();
}

// Utility to generate form link
export function getFormPublicUrl(submissionId: string): string {
  return `${window.location.origin}/forms/${submissionId}`;
}

// Copy form link to clipboard
export async function copyFormLink(submissionId: string): Promise<void> {
  const url = getFormPublicUrl(submissionId);
  await navigator.clipboard.writeText(url);
}

// ============================================================
// FORM TRIGGERS
// ============================================================

// Backend expects these values - frontend uses different display values
export type DeliveryMethod = 'EMAIL' | 'EMAIL_HTML' | 'SMS';
export type TriggerInstanceStatus = 'scheduled' | 'sent' | 'cancelled' | 'failed';

export interface FormTrigger {
  id: string;
  form_template_id: string;
  minutes_before: number;
  delivery_method: DeliveryMethod;
  is_enabled: boolean;
  message_template?: string;
  template_id?: string | null;
  from_email_address_id?: string | null;
  created_at: string;
  updated_at: string;
}

export interface FormTriggerWithTemplate extends FormTrigger {
  form_template_name: string;
}

export interface FormTriggerInstance {
  id: string;
  form_trigger_id: string;
  form_submission_id: string;
  scheduled_time: string;
  status: TriggerInstanceStatus;
  eventbridge_schedule_name?: string;
  sent_at?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

// Get triggers for a template
export async function getFormTriggers(templateId: string): Promise<FormTrigger[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/${templateId}/triggers`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch form triggers');
  return response.json();
}

// Get all triggers across all templates for a practice
export async function getAllFormTriggers(): Promise<FormTriggerWithTemplate[]> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/triggers`, {
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to fetch all form triggers');
  return response.json();
}

// Create a trigger
export async function createFormTrigger(
  templateId: string,
  data: {
    minutes_before: number;
    delivery_method: DeliveryMethod;
    is_enabled?: boolean;
    message_template?: string;
    template_id?: string | null;
    from_email_address_id?: string | null;
  }
): Promise<FormTrigger> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/templates/${templateId}/triggers`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) throw new Error('Failed to create form trigger');
  return response.json();
}

// Update a trigger
export async function updateFormTrigger(
  triggerId: string,
  data: Partial<{
    minutes_before: number;
    delivery_method: DeliveryMethod;
    is_enabled: boolean;
    message_template?: string;
    template_id?: string | null;
    from_email_address_id?: string | null;
  }>
): Promise<FormTrigger> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/triggers/${triggerId}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) throw new Error('Failed to update form trigger');
  return response.json();
}

// Delete a trigger
export async function deleteFormTrigger(triggerId: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/v1/forms/triggers/${triggerId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  
  if (!response.ok) throw new Error('Failed to delete form trigger');
}

// Helper to convert minutes to human-readable format
export function formatTriggerTime(minutes: number): string {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return `${days} day${days > 1 ? 's' : ''} before`;
  } else if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    return `${hours} hour${hours > 1 ? 's' : ''} before`;
  } else {
    return `${minutes} minute${minutes > 1 ? 's' : ''} before`;
  }
}

